// AssignCoverAction — an APPROVER arranges cover for a report's time off.
//
// ⚠️ THE GAP. A delegation's delegator was always the person who created it,
// so a manager could never name cover for someone else: requests read "No
// cover assigned yet" / "Cover needs attention" and the approver had nothing
// to press. This puts "Assign cover" next to that status: pick a colleague,
// confirm (branded), and leave-service PUT /leave-requests/:id/cover asks auth
// for a COVERAGE-ONLY delegation — the person covers the work, no approvals
// are delegated — which they are asked to accept. The employee is told.
//
// Cover still in place (asked, accepted, or unaccepted near the start) is
// REPLACED, and the confirm says the current person will be told. Once cover
// was assigned the card used to offer only Approve/Deny (QA C5), so the
// approver could never swap the person. Server refusals (no
// login, already arranged, not your report…) are shown as the server words them.
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { UserPlus } from 'lucide-react';
import { getApiErrorMessage } from '@/lib/apiError';
import { assignLeaveCover } from '@/lib/hrApi';
import { searchEmployees } from '@/services/workforceService';
import { useConfirmIfAvailable } from '@/features/shared/components/confirm/ConfirmProvider';
import { PeoplePicker, type EligiblePage, type Person } from '@/features/shared/components/PeoplePicker';
import { Button } from '@/features/shared/components/ui/button';

/** A delegate is still named and not dead — assigning someone else replaces
 *  them (they are told). Declined / cancelled / none named → nothing to replace;
 *  cover that already ran its course has nothing left to hand over. */
export function coverStillInPlace(status?: string | null, reason?: string | null): boolean {
  return status === 'pending'
    || (status === 'covered' && reason !== 'ran_its_course')
    || (status === 'needs_attention' && reason === 'unaccepted_near_start');
}

/** Show "Assign cover" when cover is missing / needs attention, and "Replace
 *  cover" while someone is still named (leave-service derived statuses). */
export function canAssignCover(status?: string | null, reason?: string | null): boolean {
  return status === 'unassigned' || status === 'needs_attention' || coverStillInPlace(status, reason);
}

export interface AssignCoverResult {
  coverage_status?: string | null;
  coverage_reason?: string | null;
  coverage_delegate_name?: string | null;
}

export interface AssignCoverActionProps {
  requestId: string;
  employeeName: string;
  /** The requester's auth user id — never offered as their own cover. */
  employeeUserId?: string | null;
  dates: string;
  coverageStatus?: string | null;
  coverageReason?: string | null;
  coverageDelegateName?: string | null;
  onAssigned: (r: AssignCoverResult) => void;
  /** Directory search seam (tests). */
  fetchPage?: (q: string, page: number) => Promise<EligiblePage>;
}

const directoryPage = async (q: string, exceptUserId?: string | null): Promise<EligiblePage> => {
  const hits = q.trim() ? await searchEmployees(q) : [];
  const items: Person[] = hits
    // The delegation stores the employee RECORD uuid; a hit without one
    // (only an employee number) can't be sent.
    .filter((h) => !!h.employee_uuid && (!exceptUserId || h.user_id !== exceptUserId))
    .map((h) => ({ employee_id: h.employee_uuid as string, user_id: h.user_id ?? null, display_name: h.name, job_title: h.sublabel || null }));
  return { items, total: items.length, page: 1, page_size: items.length || 10, has_more: false, reason: 'OK', scope_summary: '' };
};

export const AssignCoverAction: React.FC<AssignCoverActionProps> = ({
  requestId, employeeName, employeeUserId, dates, coverageStatus, coverageReason, coverageDelegateName, onAssigned, fetchPage,
}) => {
  const { t } = useTranslation(['timeoff']);
  const confirm = useConfirmIfAvailable();
  const [open, setOpen] = useState(false);
  const [person, setPerson] = useState<Person | null>(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  if (!canAssignCover(coverageStatus, coverageReason)) return null;
  const replace = coverStillInPlace(coverageStatus, coverageReason);
  const k = (key: string, fallback: string, o: Record<string, unknown> = {}) => t(`timeoff:${key}`, { defaultValue: fallback, ...o }) as string;

  const submit = async () => {
    if (!person) return;
    const vars = { person: person.display_name, name: employeeName, dates, current: coverageDelegateName || k('assign_cover_current_fallback', 'The current cover') };
    if (confirm) {
      const ok = await confirm({
        title: k('assign_cover_confirm_title', 'Ask {{person}} to cover?', vars),
        message: [
          k('assign_cover_confirm_body', "{{person}} will be asked to cover {{name}}'s time off ({{dates}}) and can accept or decline. {{name}} will be told. No approvals are delegated — this is cover only.", vars),
          replace ? k('assign_cover_confirm_replace', "{{current}} will be told they're no longer needed.", vars) : '',
        ].filter(Boolean).join(' '),
        confirmLabel: k('assign_cover_confirm', 'Ask to cover'),
        cancelLabel: k('assign_cover_cancel', 'Cancel'),
      });
      if (!ok) return;
    }
    setSaving(true);
    try {
      const updated = (await assignLeaveCover(requestId, {
        delegateEmployeeId: person.employee_id,
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(replace ? { replace: true } : {}),
      })) as AssignCoverResult & { coverage?: { delegate_name?: string | null } | null };
      toast.success(k('assign_cover_done', '{{person}} has been asked to cover. {{name}} has been told.', vars));
      onAssigned({
        coverage_status: updated?.coverage_status ?? null,
        coverage_reason: updated?.coverage_reason ?? null,
        coverage_delegate_name: updated?.coverage?.delegate_name ?? person.display_name,
      });
      setOpen(false);
      setPerson(null);
      setNote('');
    } catch (e) {
      toast.error(getApiErrorMessage(e, k('assign_cover_failed', "Couldn't arrange cover.")), { duration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <Button type="button" size="sm" variant="outline" className="mt-1.5" onClick={() => setOpen(true)} data-testid="assign-cover">
        <UserPlus className="h-3.5 w-3.5 mr-1" />
        {replace ? k('assign_cover_replace', 'Replace cover') : k('assign_cover', 'Assign cover')}
      </Button>
    );
  }
  const inputId = `assign-cover-${requestId}`;
  return (
    <div className="mt-2 space-y-2 rounded-lg border border-border bg-card p-2.5" data-testid="assign-cover-panel">
      <label htmlFor={inputId} className="block text-xs font-semibold text-foreground">
        {k('assign_cover_who', "Who will cover {{name}}'s time off?", { name: employeeName })}
      </label>
      <PeoplePicker
        id={inputId}
        size="sm"
        value={person}
        onChange={setPerson}
        fetchPage={fetchPage ?? ((q: string) => directoryPage(q, employeeUserId))}
        disabled={saving}
        placeholder={k('assign_cover_search', 'Search a colleague by name…')}
        emptyHint={k('assign_cover_search_hint', 'Type a name to search the company directory.')}
      />
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={2}
        maxLength={2000}
        disabled={saving}
        aria-label={k('assign_cover_note', 'Handover note (optional)')}
        placeholder={k('assign_cover_note', 'Handover note (optional)')}
        className="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-xs text-foreground outline-none focus:border-ring resize-none"
      />
      <p className="m-0 text-[11px] text-muted-foreground">
        {k('assign_cover_hint', "They'll be asked to accept. Cover only — no approvals are delegated.")}
      </p>
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="ghost" disabled={saving} onClick={() => { setOpen(false); setPerson(null); setNote(''); }}>
          {k('assign_cover_cancel', 'Cancel')}
        </Button>
        <Button type="button" size="sm" disabled={!person || saving} onClick={submit} data-testid="assign-cover-submit">
          {saving ? k('assign_cover_saving', 'Asking…') : k('assign_cover_confirm', 'Ask to cover')}
        </Button>
      </div>
    </div>
  );
};

export default AssignCoverAction;
